import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { RootState } from "~/store";
import { updateSingleThunk } from "~/thunks/updateThunks";
import { uploadImageThunk } from "~/thunks/uploadThunk";
import type { DbDataType } from "~/types/general";
import type { CurrentUser, OrganizationRole } from "~/types/roles";

interface UserState {
  currentUser: CurrentUser | null;
  currentUserRole: OrganizationRole | null;
  createdLoginId: number | null;
}

const initialState: UserState = {
  currentUser: null,
  currentUserRole: null,
  createdLoginId: null
};

const LOGOUT_TIMEOUT_MS = 3000; // 12 hours
let logoutTimeout: ReturnType<typeof setTimeout> | null = null;

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    userLoaded: (state, action: PayloadAction<{ user: CurrentUser; role: OrganizationRole; createdLoginId: number }>) => {
      console.log("userLoaded", action.payload);
      localStorage.setItem("currentUser", JSON.stringify(action.payload));
      state.currentUser = action.payload.user;
      let role = action.payload.role;
      // role.default_permitted_tasks = 'Create folder structure,Drone based bridge survey,Upload images'
      state.currentUserRole = role;
      state.createdLoginId = action.payload.createdLoginId;
    },
    userLoggedOut: (state) => {
      console.log("userLoggedOut");
      state.currentUser = null;
      state.currentUserRole = null;
      localStorage.removeItem("currentUser");
      if (logoutTimeout) clearTimeout(logoutTimeout);
      logoutTimeout = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateSingleThunk.fulfilled, (state, action: PayloadAction<{
        dataType: string,
        data: any
      }>) => {
        if (action.payload.dataType !== 'users') return;
        console.log("updateUser fulfilled", action.payload);
        console.log("Before update, currentUser:", state.currentUser);
        if (state.currentUser && state.currentUser.id === action.payload.data.id) {
          const updatedUser = { ...state.currentUser, ...action.payload.data };
          state.currentUser = updatedUser;
          localStorage.setItem("currentUser", JSON.stringify({ user: updatedUser, role: state.currentUserRole }));
          console.log("After update, currentUser:", state.currentUser);
        }
      })
      .addCase(uploadImageThunk.fulfilled, (state, action: PayloadAction<any>) => {
        console.log("uploadImageThunk fulfilled", action.payload);
        if (action.payload.dataType === 'assets' && action.payload.data.bid) {
          // Update organization profile image
          if (state.currentUser && state.currentUser.id === action.payload.data.id) {
            console.log("Updating asset image_url in assetSlice", action.payload.data.image_url);
            state.currentUser = {
              ...state.currentUser,
              profile_image: action.payload.data.image_url
            };
            localStorage.setItem("currentUser", JSON.stringify({ user: state.currentUser, role: state.currentUserRole }));
          }
        }
      })
  }
});

export const { userLoaded, userLoggedOut } = userSlice.actions;
export default userSlice.reducer;
export const selectCurrentUser = (state: RootState) => state.user.currentUser;
export const selectCurrentUserRole = (state: RootState) => state.user.currentUserRole;